/**
 * The worn equipment panel.
 *
 * Eleven slots laid out roughly where they sit on a person, and the bonuses those
 * items add up to. The totals come from the server, because the server is the only
 * side that decides what an item is worth.
 */

import { EQUIPMENT_SLOT_NAMES, TOOLBELT_SLOTS, type ItemStack } from '@inverse/common';
import type { BonusesPacket, ItemDefinitionWire } from '@inverse/protocol';
import { useCallback, useRef, useState } from 'react';
import { CloseButton } from './CloseButton';
import { useDraggableWindow } from './draggable';
import { useLongPress } from './longPress';
import { shortQuantity } from './slots';

/**
 * Where each slot sits in the grid, and what it is called.
 *
 * Laid out roughly where the thing sits on a person. Hands and ring share the
 * row with the legs, since a row holding one boot and two spaces is a row of
 * mostly nothing. Weapon and shield are named the way a person names them.
 */
const LAYOUT: Readonly<Record<string, { column: number; row: number; label: string }>> = {
  head: { column: 2, row: 1, label: 'Head' },
  cape: { column: 1, row: 2, label: 'Cape' },
  neck: { column: 2, row: 2, label: 'Neck' },
  ammunition: { column: 3, row: 2, label: 'Ammunition' },
  weapon: { column: 1, row: 3, label: 'Main Hand' },
  body: { column: 2, row: 3, label: 'Body' },
  shield: { column: 3, row: 3, label: 'Off Hand' },
  hands: { column: 1, row: 4, label: 'Hands' },
  legs: { column: 2, row: 4, label: 'Legs' },
  ring: { column: 3, row: 4, label: 'Ring' },
  feet: { column: 2, row: 5, label: 'Feet' },
};

/** The bonus fields, which are the numbers. The rest of the packet is not. */
type BonusField = Exclude<
  keyof BonusesPacket,
  'style' | 'retaliate' | 'maxHit' | 'speed' | 'accuracy' | 'stance'
>;

const BONUS_ROWS: readonly { label: string; fields: readonly BonusField[] }[] = [
  { label: 'Attack', fields: ['attackStab', 'attackSlash', 'attackBash'] },
  { label: 'Ranged and magic', fields: ['attackRanged', 'attackMagic'] },
  { label: 'Defence', fields: ['defenceStab', 'defenceSlash', 'defenceBash'] },
  { label: 'Against ranged and magic', fields: ['defenceRanged', 'defenceMagic'] },
  { label: 'Strength', fields: ['strength', 'rangedStrength'] },
  { label: 'Magic damage and faith', fields: ['magicDamage', 'prayer'] },
];

const signed = (value: number): string => (value > 0 ? `+${value}` : String(value));

function tint(itemId: number): string {
  return `hsl(${(itemId * 47) % 360} 38% 38%)`;
}

function initials(name: string): string {
  const words = name.split(' ').filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase();
  return `${(words[0] ?? '').slice(0, 1)}${(words[1] ?? '').slice(0, 1)}`.toUpperCase();
}

export function Equipment({
  slots,
  bonuses,
  combatLevel,
  onHoverText,
  describe,
  onRemove,
  toolbelt,
  onBeltMenu,
  onHover,
  onMenu,
  onClose,
  attached = false,
  onDetach,
}: {
  slots: readonly (ItemStack | null)[];
  bonuses: BonusesPacket;
  /** What the character fights at, which belongs with the rest of their numbers. */
  combatLevel: number;
  /** What the pointer is resting on, for anything explained rather than labelled. */
  onHoverText?: (held: { title: string; lines: string[] } | null) => void;
  describe: (itemId: number) => ItemDefinitionWire | undefined;
  onRemove: (slot: number) => void;
  /** What the pointer is resting on, so a tooltip can say what it is. */
  onHover?: (itemId: number | null) => void;
  onMenu: (slot: number, screenX: number, screenY: number) => void;
  /** What is on the belt, and what a right click on one of them opens. */
  toolbelt: readonly (ItemStack | null)[];
  onBeltMenu: (slot: number, screenX: number, screenY: number) => void;
  onClose: () => void;
  /**
   * Whether it sits against the bag rather than floating free.
   *
   * Attached is the ordinary way round: a drawer that slides out beside the bag,
   * the same height as it, which is what a kit is on a screen laid out the way
   * the old game laid one out. Taking it off makes it a window that goes
   * anywhere.
   */
  attached?: boolean;
  /** Swaps between the two, or nothing where that is not offered. */
  onDetach?: () => void;
}): JSX.Element {
  const pressed = useRef(-1);

  /** Whether the fourteen bonus numbers are showing. */
  const [stats, setStats] = useState(false);

  /** And whether the belt is. */
  const [belt, setBelt] = useState(false);

  const hold = useLongPress((x, y) => {
    if (pressed.current >= 0) onMenu(pressed.current, x, y);
  });

  // Where it sat before it could be moved: beside the bag, above the bottom edge.
  // After that it is wherever the player put it.
  const home = useCallback((size: { width: number; height: number }) => {
    const dock = document.querySelector('.dock')?.getBoundingClientRect();
    const right = dock ? window.innerWidth - dock.left : 248;

    return {
      x: window.innerWidth - size.width - right - 10,
      y: window.innerHeight - size.height - 12,
    };
  }, []);

  const { attach, style, onPointerDown } = useDraggableWindow('equipment', { home });

  // Attached, it is a drawer that slides out beside the bag and is the same
  // height as the panel it sits against; detached, it is a window like any
  // other. The hook runs either way, because a hook that runs some of the time
  // is not a hook.
  return (
    <div
      className={attached ? 'equipment attached' : 'equipment'}
      ref={attached ? undefined : attach}
      style={attached ? undefined : style}
    >
      <div className="panel-head drag-handle" onPointerDown={attached ? undefined : onPointerDown}>
        <span>Equipment</span>
        <span className="panel-head-acts">
          {onDetach ? (
            <button
              type="button"
              className="panel-pin"
              title={attached ? 'Take it off the bag' : 'Put it back beside the bag'}
              onClick={onDetach}
            >
              {attached ? '\u2197' : '\u2199'}
            </button>
          ) : null}
          <CloseButton onClose={onClose} />
        </span>
      </div>

      <div className="equipment-grid">
        {EQUIPMENT_SLOT_NAMES.map((name, slot) => {
          const stack = slots[slot] ?? null;
          const definition = stack ? describe(stack.id) : undefined;
          const label = definition?.name ?? (stack ? `Item ${stack.id}` : name);
          const place = LAYOUT[name] ?? { column: 2, row: 6, label: name };

          return (
            <div
              key={name}
              className={stack ? 'slot filled' : 'slot'}
              style={{ gridColumn: place.column, gridRow: place.row }}
              data-slot={slot}
              data-name={stack ? label : ''}
              onPointerEnter={() => onHover?.(stack ? stack.id : null)}
              onPointerLeave={() => onHover?.(null)}
              onPointerDown={(event) => {
                pressed.current = stack ? slot : -1;
                hold.onPointerDown(event);
              }}
              onPointerMove={hold.onPointerMove}
              onPointerUp={hold.onPointerUp}
              onPointerCancel={hold.onPointerCancel}
              onClick={() => {
                if (stack && !hold.consumed()) onRemove(slot);
              }}
              onContextMenu={(event) => {
                event.preventDefault();
                if (stack) onMenu(slot, event.clientX, event.clientY);
              }}
            >
              {stack ? (
                <>
                  <span className="slot-icon" style={{ background: tint(stack.id) }}>
                    {initials(label)}
                  </span>
                  {/* Arrows are worn by the fistful, and how many are left is the
                      whole question an archer has about them. */}
                  {definition?.stackable || stack.quantity > 1 ? (
                    <span className="slot-quantity">{shortQuantity(stack.quantity)}</span>
                  ) : null}
                </>
              ) : (
                <span className="slot-empty">{place.label}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* What the character comes to, which is what somebody opens this to see:
          how hard they hit, how often, and how likely it is to land. */}
      <div className="bonuses">
        <div className="bonus-row combat">
          <span
            onPointerEnter={() =>
              onHoverText?.({
                title: `Combat level ${combatLevel}`,
                lines: [
                  'A quarter of Defence and Hitpoints, plus the better of your fighting styles.',
                  'Melee counts Attack and Strength at just over a third each; ranged and magic count their own level at half again.',
                ],
              })
            }
            onPointerLeave={() => onHoverText?.(null)}
          >
            Combat level
          </span>
          <strong>{combatLevel}</strong>
        </div>

        {/* The belt, opened from here because that is where somebody looks for
              what they are carrying about with them, and sitting just above the
              stats because both are things folded away until they are wanted. */}
        <button
          type="button"
          className="belt-open"
          aria-expanded={belt}
          onClick={() => setBelt(!belt)}
        >
          <span>Toolbelt</span>
          <span className="bonus-chevron">{belt ? '\u25be' : '\u25b8'}</span>
        </button>

        {belt ? (
          <div className="belt-grid">
            {TOOLBELT_SLOTS.map((place, slot) => {
              const stack = toolbelt[slot] ?? null;
              const definition = stack ? describe(stack.id) : undefined;
              const label = definition?.name ?? (stack ? `Item ${stack.id}` : place.label);

              return (
                <div
                  key={place.tag}
                  className={stack ? 'slot filled' : 'slot'}
                  title={stack ? label : `No ${place.label.toLowerCase()} on your belt`}
                  onPointerEnter={() => onHover?.(stack ? stack.id : null)}
                  onPointerLeave={() => onHover?.(null)}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    if (stack) onBeltMenu(slot, event.clientX, event.clientY);
                  }}
                >
                  {stack ? (
                    <span className="slot-icon" style={{ background: tint(stack.id) }}>
                      {initials(label)}
                    </span>
                  ) : (
                    <span className="slot-empty">{place.label}</span>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Everything but the combat level is folded away by default.
            What somebody opens this panel for is what they are wearing; how
            hard it hits and what it comes to are questions asked while
            comparing two pieces of armour, which is not most of the time. */}
        <button
          type="button"
          className="bonus-fold"
          aria-expanded={stats}
          onClick={() => setStats(!stats)}
        >
          <span>Stats</span>
          <span className="bonus-chevron">{stats ? '\u25be' : '\u25b8'}</span>
        </button>

        {stats ? (
          <>
            <div className="bonus-row">
              <span>Hardest blow</span>
              <strong>{bonuses.maxHit}</strong>
            </div>
            <div className="bonus-row">
              <span>Swinging</span>
              <strong>
                {bonuses.style}, every {(bonuses.speed * 0.6).toFixed(1)}s
              </strong>
            </div>
            <div className="bonus-row">
              <span>Attack roll</span>
              <strong>{bonuses.accuracy}</strong>
            </div>
          </>
        ) : null}

        {stats
          ? BONUS_ROWS.map((row) => (
              <div key={row.label} className="bonus-row">
                <span>{row.label}</span>
                <span>{row.fields.map((field) => signed(bonuses[field])).join(' ')}</span>
              </div>
            ))
          : null}
      </div>
    </div>
  );
}
